/**
 * The push-notification CONTRACT — what this repo's triggers, the mobile client
 * and the web client must agree on. Delivery types (a device target,
 * the rendered envelope, the transport seam) are not here: delivery is the push
 * service's, and `deliver.ts` is how this repo asks it for a push.
 *
 * This module is deliberately **pure TypeScript with no runtime imports**.
 * The Cloudflare worker typechecks under `flashka-web/tsconfig.party.json`
 * (`lib: ["es2023"]` + `@cloudflare/workers-types`, no DOM, no React) and pulls
 * shared code in transitively through imports. Server code must deep-import
 * `@flashka/shared/push/<module>`, never a barrel that could later grow a
 * React dependency.
 */

/** Which client an event came from. `'web'` is a browser with web push. */
export type PushPlatform = 'ios' | 'android' | 'web';

/**
 * Re-exported so this module is the one place to import the push contract
 * from. It is DERIVED from `PUSH_CATEGORY_LANE`, because a category and the
 * lane it runs in are one decision, and two lists would have to be kept in
 * agreement by hand. Type-only, so the "no runtime imports" rule above still
 * holds.
 */
export type { PushCategory } from './categories';

/**
 * The longest `pushId` the push service accepts — MIRRORED from its `wire.ts`
 * (`PUSH_ID_MAX`), which refuses a longer one on a deliver AND on an event
 * report. One copy here, so the sender (`deliver.ts`) and the tap reader
 * (`tap.ts`) cannot drift apart when the service's limit moves.
 */
export const PUSH_ID_MAX = 128;

/**
 * Where tapping a notification should land.
 *
 * A platform-neutral DESCRIPTOR, never an href — `shared/` cannot import
 * either client's router. Mobile maps it through `paths.*`
 * (`flashka-app/src/lib/navigation/routes.ts`); web will map it to a URL for
 * `webpush.fcm_options.link`.
 *
 * `sgDeck` is the one target that cannot be resolved without a hydrated sync
 * store — it resolves to the viewer's LOCAL MIRROR of a group deck, which may
 * not exist on this device at all. That is why the fallback below is part of
 * the contract rather than an afterthought.
 */
export type PushRouteTarget =
  | { kind: 'studyGroup'; groupId: string }
  | { kind: 'sgDeck'; groupId: string; sgDeckId: string; view?: 'quizzes' }
  | { kind: 'profile'; locator: string }
  | { kind: 'notificationCentre' }
  /**
   * An arbitrary in-app path (`/dashboard`, `/deck/abc`), for campaigns whose
   * destination is not one of the typed targets. Clients resolve it through
   * their own route table; an app that predates the kind is expected to fall
   * back to the notification centre rather than guess.
   */
  | { kind: 'path'; path: string };

/**
 * How long sign-out waits to take this device off the push list before going
 * ahead anyway, on every client. Sign-out is the user's; the unregister is a
 * courtesy the service also covers (FCM `UNREGISTERED`, the 270-day sweep), so
 * a hung request must never keep someone signed in.
 */
export const PUSH_SIGN_OUT_BUDGET_MS = 3_000;

/**
 * The push service's CLIENT API, relative to the AUTH WORKER's origin — not
 * this repo's `/api`. A signed-in client talks to it directly with its session
 * (mobile: the auth base URL + session cookie, as `sync-purchase.ts` does; web:
 * through its same-origin auth proxy).
 *
 * Each path is here because something in this repo calls it. A path with no
 * caller would be a second source of truth for a shape that lives in the push
 * service, and nothing here would catch it drifting.
 *
 * `devices` is called by the device registration, `preferences` by the
 * settings screens, `events` by the mobile reporter. The rules behind them —
 * what a registration must carry, which switch is a consent record, who may
 * write one — are the service's, and `preferences.ts` parses its answers
 * rather than restating them.
 */
export const PUSH_SERVICE_PATHS = {
  /** POST `{ events }` — delivered / opened / dismissed / …; answers 202. */
  events: '/api/push/events',
  /** POST a `PushDeviceRegistration`. Refused for an impersonated session. */
  devices: '/api/push/devices',
  /** POST `{ installationId }` on sign-out, or when permission is revoked. */
  devicesUnregister: '/api/push/devices/unregister',
  /** GET the switches; PUT a patch of one or both. GET is readable while
   *  impersonating (support needs to SEE them); PUT never is. */
  preferences: '/api/push/preferences',
} as const;

/**
 * Where a device lives — the app (iOS + Android) or a browser. Each surface has
 * its OWN two switches, and they govern only its own devices: someone who
 * never turned push on in a browser still gets pushes on their phone, and
 * switching the browser off never silences the phone. The push service keys
 * the switches by it (`?surface=`; none means `mobile`).
 */
export type PushSurface = 'mobile' | 'web';

/** The preferences endpoint for one surface's switches. `base` is the path on
 *  whatever origin the client reaches the service through. */
export function pushPreferencesPath(
  surface: PushSurface,
  base: string = PUSH_SERVICE_PATHS.preferences,
): string {
  return `${base}?surface=${surface}`;
}
